import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserProvider, useUser } from "../provider/UserProvider";
import { useProtocols } from "../provider/ProtocolsProvider";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Autocomplete,
  Avatar,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import axiosInstance from "../utlis/axiosInstance";

// Category options with icons
const CATEGORY_ICON_OPTIONS = [
  { label: "Car", value: "Car", icon: "/car/green.png" },
  { label: "Bus", value: "Bus", icon: "/bus/bus-blue.png" },
  { label: "Dog", value: "Dog", icon: "/dog/dog-blue.png" },
  { label: "Men", value: "Men", icon: "/men/men-blue.png" },
  { label: "Swag", value: "Swag", icon: "/swag/swag-blue.png" },
  { label: "Tractor", value: "Tractor", icon: "/tractor/tractor-blue.png" },
  { label: "Two Wheeler", value: "TwoWheeler", icon: "/twoWheeler/twoWheeler-blue.png" },
  { label: "Women", value: "Women", icon: "/women/women-blue.png" },
];

const CATEGORY_OPTIONS = CATEGORY_ICON_OPTIONS.map(option => option.value);



interface DeviceFormData {
  name: string;
  unique_id: string;
  status: string;
  disabled: boolean;
  phone: string;
  sim_number: "",
  model: string;
  contact: string;
  category: string;
  position_id: number;
  group: number | null;
  inventory: boolean;
  odometer: number;
  attributes: any;
  expiration_time: string | null;
}

interface DocumentData {
  type: string;
  file: File | null;
  fileName: string;
}

const AddDeviceInventory: React.FC = () => {
  const navigate = useNavigate();
  const { permissions } = useUser();
  const { protocols, loading: protocolsLoading } = useProtocols();
  const { id } = useParams<{ id?: string }>();
  const isEdit = id !== undefined;
  const deviceId = id ? Number(id) : null;

  // Permission checks
  const canAddDevice = permissions?.includes("DEVICE_ADD");
  const canUpdateDevice = permissions?.includes("DEVICE_UPDATE");
  const hasRequiredPermission = isEdit ? canUpdateDevice : canAddDevice;

  // Move all hooks before the permission check
  const [form, setForm] = useState<DeviceFormData>({
    name: "",
    unique_id: "",
    status: "active",
    disabled: false,
    phone: "",
    sim_number: "",
    model: "",
    contact: "",
    category: "Car",
    position_id: 101,
    group: null,
    inventory: true,
    odometer: 0,
    expiration_time: null,
    attributes: {
      battery: null,
      color: null,
      port: {},
      mdn_number: "",
      customer_name: "",
      odometer: 0,
      traveled_distance: 0
    }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Protocol selection state
  const [selectedProtocol, setSelectedProtocol] = useState<any>(null);
  const [attributesState, setAttributesState] = useState<{ [key: string]: boolean }>({});

  // Convert protocols to dropdown items
  const protocolItems = protocols.map((protocol: any) => ({
    label: protocol.port ? `${protocol.protocol} (Port: ${protocol.port})` : protocol.protocol,
    value: protocol.id.toString(),
    protocol: protocol
  }));

  useEffect(() => {
    if (isEdit && deviceId) {
      fetchDeviceDetails(deviceId);
    }
  }, [isEdit, deviceId]);

  const fetchDeviceDetails = async (id: number) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/gpstracker/devicehandler/${id}`
      );
      const device = response.data?.responseData || response.data;

      // Convert date string to YYYY-MM-DD format
      let expirationTime = null;
      if (device.expiration_time) {
        const date = new Date(device.expiration_time);
        expirationTime = date.toISOString().split('T')[0]; // Format: YYYY-MM-DD
      }

      setForm({
        name: device.name || "",
        unique_id: (device.unique_id || "").replace(/\s/g, '').trim(),
        status: device.status || "active",
        disabled: device.disabled || false,
        phone: device.phone || "",
        sim_number: device.sim_number || "",
        model: device.model || "",
        contact: device.contact || "",
        category: device.category || "Car",
        position_id: device.position_id || 101,
        group: device.group,
        inventory: device.inventory || true,
        odometer: (typeof device.odometer === 'number' ? device.odometer : (device.attributes?.odometer || 0)) || 0,
        expiration_time: expirationTime,
        attributes: {
          battery: device.attributes?.battery || null,
          color: device.attributes?.color || null,
          port: device.attributes?.port || {},
          mdn_number: device.attributes?.mdn_number || "",
          customer_name: device.attributes?.customer_name || "",
          odometer: device.attributes?.odometer ?? (typeof device.odometer === 'number' ? device.odometer : 0),
          traveled_distance: device.attributes?.traveled_distance ?? device.traveled_distance ?? 0
        }
      });

      // Set protocol selection based on model
      if (device.model && protocols.length > 0) {
        const matchingProtocol = protocols.find((p: any) => p.protocol === device.model);
        if (matchingProtocol) {
          setSelectedProtocol(matchingProtocol);

          // Initialize attributes state from existing port data
          if (device.attributes?.port) {
            setAttributesState(device.attributes.port);
          } else {
            // Set default attributes if no existing port data
            const defaultAttributes: { [key: string]: boolean } = {};
            if (matchingProtocol.attributes) {
              Object.keys(matchingProtocol.attributes).forEach(key => {
                if (matchingProtocol.attributes[key] === true) {
                  defaultAttributes[key] = true;
                }
              });
            }
            setAttributesState(defaultAttributes);
          }
        }
      }
    } catch (error) {
      setError("Failed to fetch device details");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field: keyof DeviceFormData, value: any) => {
    if (field === 'expiration_time' && value) {
      // Keep only the date part (YYYY-MM-DD)
      value = value.split('T')[0];
    }
    if (field === 'odometer') {
      value = Number(value) || 0;
    }
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleProtocolChange = (protocolId: string | null) => {
    if (protocolId) {
      const selectedProtocol = protocols.find((p: any) => p.id.toString() === protocolId);
      if (selectedProtocol) {
        setSelectedProtocol(selectedProtocol);
        handleFormChange("model", selectedProtocol.protocol);

        // Initialize attributes with only true values
        const defaultAttributes: { [key: string]: boolean } = {};
        if (selectedProtocol.attributes) {
          Object.keys(selectedProtocol.attributes).forEach(key => {
            if (selectedProtocol.attributes[key] === true) {
              defaultAttributes[key] = true;
            }
          });
        }
        setAttributesState(defaultAttributes);

        // Save attributes to form with port object
        setForm(prev => ({
          ...prev,
          attributes: {
            ...prev.attributes,
            port: defaultAttributes
          }
        }));
      }
    } else {
      setSelectedProtocol(null);
      setAttributesState({});
      handleFormChange("model", "");
    }
  };

  const handleAttributeChange = (attributeKey: string, value: boolean) => {
    const newAttributes = {
      ...attributesState,
      [attributeKey]: value
    };
    setAttributesState(newAttributes);

    const updatedForm = {
      ...form,
      attributes: {
        ...form.attributes,
        port: newAttributes
      }
    };
    setForm(updatedForm);
  };



  const handleSubmit = async () => {
    try {
      // Check permissions again before submission
      if (isEdit && !canUpdateDevice) {
        setError("You don't have permission to update devices");
        return;
      }
      if (!isEdit && !canAddDevice) {
        setError("You don't have permission to add devices");
        return;
      }

      setLoading(true);
      setError(null);
      setSuccess(null);

      // Trim and clean unique_id before validation
      const cleanedUniqueId = form.unique_id.replace(/\s/g, '').trim();
      
      if (!form.name || !cleanedUniqueId || !form.model) {
        setError(
          "Please fill in all required fields (Vehicle Number, IMEI/ID, Model)"
        );
        return;
      }

      if (!form.attributes?.customer_name || !String(form.attributes.customer_name).trim()) {
        setError("Customer name is required");
        return;
      }

      if (form.sim_number && !/^\d*$/.test(form.sim_number)) {
        setError("Phone number must contain only digits");
        return;
      }

      const deviceData = {
        ...form,
        unique_id: cleanedUniqueId,
        updated_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        odometer: Number(form.odometer) || 0,
        attributes: {
          ...form.attributes,
          odometer: Number(form.odometer) || 0,
          mdn_number: form.attributes?.mdn_number || '',
          traveled_distance: Number(form.attributes?.traveled_distance) || 0,
        }
      };

      if (isEdit && deviceId) {
        await axiosInstance.put(
          `/gpstracker/devicehandler/${deviceId}`,
          deviceData
        );
        setSuccess("Device updated successfully!");
      } else {
        await axiosInstance.post("/gpstracker/devicehandler/", deviceData);
        setSuccess("Device created successfully!");
      }

      setTimeout(() => {
        navigate("/inventory");
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to save device");
    } finally {
      setLoading(false);
    }
  };

  // Show error if user doesn't have required permissions
  if (!hasRequiredPermission) {
    return (
      <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", py: 4 }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              bgcolor: "white",
              border: "1px solid",
              borderColor: "#e2e8f0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton onClick={() => navigate("/inventory")}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" color="error">
                {isEdit
                  ? "You don't have permission to edit devices"
                  : "You don't have permission to add devices"
                }
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="md">
        <Paper elevation={0} sx={{ mb: 3, p: 3, borderRadius: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate("/inventory")}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight={600}>
              {isEdit ? "Edit Device" : "Add New Device"}
            </Typography>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
            {success}
          </Alert>
        )}

        <Paper elevation={0} sx={{ p: 4, borderRadius: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
            Basic Information
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
              mb: 4,
            }}
          >
            <TextField
              fullWidth
              label="Vehicle Number *"
              value={form.name}
              onChange={(e) =>
                handleFormChange("name", e.target.value.toUpperCase())
              }
            />
            <TextField
              fullWidth
              label="IMEI / ID *"
              value={form.unique_id}
              onChange={(e) => {
                // Remove all spaces and trim the value
                const cleanedValue = e.target.value.replace(/\s/g, '').trim();
                handleFormChange("unique_id", cleanedValue);
              }}
            />
            <TextField
              fullWidth
              label="Sim Number"
              value={form.sim_number}
              onChange={(e) => handleFormChange("sim_number", e.target.value)}
            />
            <TextField
              fullWidth
              label="MDN Number"
              value={form.attributes.mdn_number}
              onChange={(e) => {
                setForm(prev => ({
                  ...prev,
                  attributes: {
                    ...prev.attributes,
                    mdn_number: e.target.value
                  }
                }));
              }}
              placeholder="Enter MDN number"
            />
            <TextField
              fullWidth
              label="Customer Name"
              value={form.attributes.customer_name}
              onChange={(e) => {
                setForm(prev => ({
                  ...prev,
                  attributes: {
                    ...prev.attributes,
                    customer_name: e.target.value
                  }
                }));
              }}
              placeholder="Enter customer name"
              required
            />
          <TextField
            fullWidth
            type="number"
            label="Odometer (KM)"
            value={form.odometer}
            onChange={(e) => {
              const val = Number(e.target.value) || 0;
              setForm(prev => ({
                ...prev,
                odometer: val,
                attributes: {
                  ...prev.attributes,
                  odometer: val
                }
              }));
            }}
            inputProps={{ min: 0, step: 0.01 }}
          />

            {/* Traveled Distance - Only show in edit mode */}
            {isEdit && (
              <TextField
                fullWidth
                type="number"
                label="Traveled Distance (KM)"
                value={form.attributes.traveled_distance ?? 0}
                onChange={(e) => {
                  const val = Number(e.target.value) || 0;
                  setForm(prev => ({
                    ...prev,
                    attributes: {
                      ...prev.attributes,
                      traveled_distance: val,
                    },
                  }));
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            )}

            {/* Model/Protocol Dropdown */}
            <Autocomplete
              fullWidth
              options={protocolItems}
              getOptionLabel={(option) => option.label}
              value={protocolItems.find(item => item.value === selectedProtocol?.id?.toString()) || null}
              onChange={(_, newValue) => {
                handleProtocolChange(newValue?.value || null);
              }}
              loading={protocolsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Model/Protocol *"
                  placeholder="Select model/protocol"
                  helperText={selectedProtocol?.port ? `Port: ${selectedProtocol.port}` : ''}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {protocolsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              fullWidth
              label="Driver Contact"
              value={form.contact}
              onChange={(e) => handleFormChange("contact", e.target.value)}
            />
            <Autocomplete
              fullWidth
              options={CATEGORY_ICON_OPTIONS}
              getOptionLabel={(option) => option.label}
              value={CATEGORY_ICON_OPTIONS.find(item => item.value === form.category) || null}
              onChange={(_, newValue) => {
                handleFormChange("category", newValue?.value || "Car");
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    src={option.icon}
                    alt={option.label}
                    sx={{ width: 24, height: 24 }}
                  />
                  <Typography>{option.label}</Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  placeholder="Select category"
                />
              )}
            />
            <TextField
              fullWidth
              type="date"
              label="Expiration Date"
              value={form.expiration_time ? form.expiration_time.split('T')[0] : ""}
              onChange={(e) => handleFormChange("expiration_time", e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>

          {/* Protocol Attributes Section */}
          {selectedProtocol && Object.keys(attributesState).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Protocol Permissions
              </Typography>
              <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "#e0e0e0" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 1 }}>
                  {Object.keys(attributesState).map((key) => (
                    <Box key={key} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1, border: "1px solid", borderColor: "#f0f0f0", borderRadius: 0.5 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={500} sx={{ textTransform: "capitalize" }}>
                          {key}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {attributesState[key] ? 'Enabled' : 'Disabled'}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={attributesState[key] || false}
                            onChange={(e) => handleAttributeChange(key, e.target.checked)}
                            color="primary"
                            size="small"
                          />
                        }
                        label=""
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              onClick={() => navigate("/inventory")}
              color="inherit"
              size="large"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              size="large"
              disabled={loading || !hasRequiredPermission}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{
                bgcolor: hasRequiredPermission ? "var(--primary-color)" : "rgba(0, 0, 0, 0.12)",
                "&:hover": { bgcolor: hasRequiredPermission ? "var(--primary-light)" : "rgba(0, 0, 0, 0.12)" },
              }}
            >
              {loading ? "Saving..." : isEdit ? "Update Device" : "Add Device"}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AddDeviceInventory;
